import type {
    PreparedReplayChunk, ReplayChunkAcknowledgement, ReplayOutboxScope,
} from '../lib/sessionReplayOutbox';
import type {ReplayCreationDescriptor} from '../lib/sessionReplayState';
import {makeSecureApiCall} from './secureApiCall';
import IDP_CONFIG from '../Auth/idp-config';
import {zitadelFirm} from '../Auth/tabSession';

export const REPLAY_MAX_RESPONSE_BYTES = 1024 * 1024;
const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;
const SQL_INT_MAX = 2_147_483_647;
type ReplayIdentity = Pick<ReplayOutboxScope, 'firmId' | 'userId'>;
type TransportCode = 'credential' | 'aborted' | 'timeout' | 'network' | 'invalid-request'
    | 'invalid-response' | 'response-too-large' | 'forbidden' | 'not-found' | 'gone'
    | 'conflict' | 'upgrade-required' | 'http' | 'session-closed';

export class ReplayTransportError extends Error {
    constructor(readonly code: TransportCode, message: string, readonly status?: number,
        readonly serverCode?: string, readonly retryAfterMs?: number) {
        super(message);
        this.name = 'ReplayTransportError';
    }

    get retryable(): boolean {
        return this.code === 'network' || this.code === 'timeout' ||
            this.status === 429 || this.status === 500 || this.status === 502 || this.status === 503 || this.status === 504;
    }
}

/** Both RFC Retry-After forms; invalid/missing values use runtime backoff. */
export function replayRetryAfter(value: string | null, now = Date.now()): number | undefined {
    if (!value?.trim()) return;
    const seconds = /^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined;
    const delay = seconds === undefined ? Date.parse(value) - now : seconds * 1000;
    return Number.isFinite(delay) && delay >= 0 ? Math.min(delay, Number.MAX_SAFE_INTEGER - now) : undefined;
}

export interface ReplayTransportOptions {signal?: AbortSignal; keepalive?: boolean}
export interface ReplayCompletion {
    summary?: unknown;
    expectedChunkCount: number;
    expectedTotalEvents: number;
}
export interface ReplaySessionResponse extends Record<string, unknown> {
    protocolVersion: 2;
    id: string;
    firmId: string;
    userId: string;
    status: 'recording' | 'completed' | 'error';
    chunkCount: number;
    totalEvents: number;
}
export interface ReplayCapabilities {
    protocolVersion: 2;
    maxUploadBytes: number;
    maxChunkPage: number;
    defaultChunkPage: number;
}

interface Dependencies {
    baseUrl: string;
    /** Synchronous current-identity check. Return no credential after logout or
     * account/firm change. Same-identity token renewal is allowed. */
    getCredential: (expected: Readonly<ReplayIdentity>) => string | null | undefined;
    /** Renew before the request when necessary. The synchronous callback above
     * remains the authority check before and after every awaited operation. */
    renewCredential?: (expected: Readonly<ReplayIdentity>, signal: AbortSignal) => Promise<void>;
    fetch?: typeof globalThis.fetch;
    timeoutMs?: number;
}

function record(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}
function invalidResponse(): ReplayTransportError {
    return new ReplayTransportError('invalid-response', 'Replay server did not confirm the expected response.');
}
function integer(value: unknown, max = SQL_INT_MAX): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= max;
}
function identity<T extends ReplayIdentity>(value: T): Readonly<T> {
    if (!value || [value.firmId, value.userId].some(part => typeof part !== 'string' || !part.trim())) {
        throw new ReplayTransportError('invalid-request', 'Replay requires a fixed firm and user.');
    }
    return Object.freeze({...value});
}
function scope(value: ReplayOutboxScope): Readonly<ReplayOutboxScope> {
    const copy = identity({firmId: value?.firmId, userId: value?.userId, sessionId: value?.sessionId});
    if (typeof copy.sessionId !== 'string' || !copy.sessionId.trim()) {
        throw new ReplayTransportError('invalid-request', 'Replay requires a fixed session.');
    }
    return copy;
}
function checkedSession(value: unknown, expected: ReplayOutboxScope): ReplaySessionResponse {
    if (!record(value) || value.protocolVersion !== 2 || value.id !== expected.sessionId ||
        value.firmId !== expected.firmId || value.userId !== expected.userId ||
        !['recording', 'completed', 'error'].includes(String(value.status)) ||
        !integer(value.chunkCount) || !integer(value.totalEvents)) throw invalidResponse();
    return value as ReplaySessionResponse;
}
function encodedJson(value: unknown): string {
    try {
        const encoded = JSON.stringify(value);
        if (encoded === undefined || new TextEncoder().encode(encoded).byteLength > REPLAY_MAX_RESPONSE_BYTES) throw new Error();
        return encoded;
    } catch {
        throw new ReplayTransportError('invalid-request', 'Replay request metadata is invalid or too large.');
    }
}

/** Local readiness only: this neither authenticates nor verifies a JWT. The
 * server remains authoritative. Do not substitute a firms-membership claim for
 * the token's exact current firmId/userId or accept an expired token. */
export function replayCredentialMatchesScope(token: unknown, expected: ReplayIdentity, nowMs = Date.now()): boolean {
    try {
        if (typeof token !== 'string' || token.length > 65536 || !record(expected) ||
            typeof expected.firmId !== 'string' || !expected.firmId ||
            typeof expected.userId !== 'string' || !expected.userId || !Number.isFinite(nowMs)) return false;
        const parts = token.split('.');
        if (parts.length !== 3 || parts.some(part => !part || !/^[A-Za-z0-9_-]+$/.test(part))) return false;
        const binary = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
        const payload: unknown = JSON.parse(new TextDecoder('utf-8', {fatal: true})
            .decode(Uint8Array.from(binary, character => character.charCodeAt(0))));
        if (!record(payload) || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp) ||
            payload.exp * 1000 <= nowMs) return false;
        if (payload.nbf !== undefined && (typeof payload.nbf !== 'number' || !Number.isFinite(payload.nbf) || payload.nbf * 1000 > nowMs)) return false;
        // A Zitadel token names the firm as its one roles-claim org and no user; the caller checks
        // the tab's server-named user (OWA-1137).
        if (IDP_CONFIG.provider === 'zitadel') return zitadelFirm(payload, IDP_CONFIG.audience) === expected.firmId;
        const metadata = payload['http://outlawpractice.com/app_metadata'];
        return record(metadata) && metadata.firmId === expected.firmId && metadata.userId === expected.userId;
    } catch { return false; }
}

/** Recorder-only transport. No persistence, retry, session allocation, index
 * advancement or outbox acknowledgement happens here. */
export class SessionReplayTransport {
    private readonly baseUrl: string;
    private readonly credential: Dependencies['getCredential'];
    private readonly renewCredential?: Dependencies['renewCredential'];
    private readonly fetcher: typeof globalThis.fetch;
    private readonly timeoutMs: number;

    constructor(deps: Dependencies) {
        if (typeof deps.baseUrl !== 'string' || !deps.baseUrl || typeof deps.getCredential !== 'function' ||
            (deps.timeoutMs !== undefined && (!Number.isFinite(deps.timeoutMs) || deps.timeoutMs <= 0))) {
            throw new ReplayTransportError('invalid-request', 'Invalid replay transport configuration.');
        }
        this.baseUrl = deps.baseUrl.replace(/\/+$/, '');
        this.credential = deps.getCredential;
        this.renewCredential = deps.renewCredential;
        this.fetcher = deps.fetch ?? globalThis.fetch.bind(globalThis);
        this.timeoutMs = deps.timeoutMs ?? 30_000;
    }

    capabilities(expected: ReplayIdentity, options: ReplayTransportOptions = {}): Promise<ReplayCapabilities> {
        const owner = identity({firmId: expected?.firmId, userId: expected?.userId});
        return this.run(owner, options, async context => {
            const value = await context.request('/replay/capabilities?protocolVersion=2');
            if (!record(value) || value.protocolVersion !== 2 || !integer(value.maxUploadBytes, MAX_UPLOAD_BYTES) ||
                value.maxUploadBytes < 1 || !integer(value.maxChunkPage, 200) || value.maxChunkPage < 1 ||
                !integer(value.defaultChunkPage, value.maxChunkPage) || value.defaultChunkPage < 1) throw invalidResponse();
            return value as unknown as ReplayCapabilities;
        });
    }

    status(expected: ReplayOutboxScope, options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        const fixed = scope(expected);
        return this.run(fixed, options, async context => checkedSession(await context.request(this.statusPath(fixed)), fixed));
    }

    create(input: ReplayCreationDescriptor, options: ReplayTransportOptions = {}, resumeOnly = false): Promise<ReplaySessionResponse> {
        const fixed = scope(input);
        const descriptor = {initialUrl: input.initialUrl, userAgent: input.userAgent,
            viewportWidth: input.viewportWidth, viewportHeight: input.viewportHeight, visitId: input.visitId,
            ...(input.continuationOf ? {continuationOf: input.continuationOf} : {}),
            ...(input.partIndex === undefined ? {} : {partIndex: input.partIndex})};
        if (typeof descriptor.initialUrl !== 'string' || typeof descriptor.userAgent !== 'string' ||
            !integer(descriptor.viewportWidth) || !integer(descriptor.viewportHeight) ||
            (descriptor.visitId !== null && typeof descriptor.visitId !== 'string')) {
            throw new ReplayTransportError('invalid-request', 'Invalid replay creation descriptor.');
        }
        const body = encodedJson({protocolVersion: 2, sessionId: fixed.sessionId, ...descriptor, ...(resumeOnly ? {resumeOnly: true} : {})});
        return this.run(fixed, options, async context => {
            const value = checkedSession(await context.request('/replay/sessions?protocolVersion=2', {method: 'POST', body}), fixed);
            const normalized = {...descriptor, initialUrl: descriptor.initialUrl || null, userAgent: descriptor.userAgent || null};
            if (Object.entries(normalized).some(([key, field]) => value[key] !== field)) throw invalidResponse();
            return value;
        });
    }

    upload(expected: ReplayOutboxScope, input: PreparedReplayChunk, options: ReplayTransportOptions = {}): Promise<ReplayChunkAcknowledgement> {
        const fixed = scope(expected);
        if (!input || input.protocolVersion !== 2 || input.sessionId !== fixed.sessionId ||
            !integer(input.chunkIndex) || !integer(input.eventCount, 200) || input.eventCount < 1 ||
            !integer(input.bytesCompressed, MAX_UPLOAD_BYTES) || input.bytesCompressed < 1 ||
            !(input.startTimestamp === null || integer(input.startTimestamp, Number.MAX_SAFE_INTEGER)) ||
            !(input.endTimestamp === null || integer(input.endTimestamp, Number.MAX_SAFE_INTEGER)) ||
            (input.startTimestamp !== null && input.endTimestamp !== null && input.endTimestamp < input.startTimestamp) ||
            typeof input.digest !== 'string' || !/^[0-9a-f]{64}$/.test(input.digest) ||
            !(input.bytes instanceof Uint8Array) || input.bytes.byteLength !== input.bytesCompressed) {
            throw new ReplayTransportError('invalid-request', 'Invalid prepared replay chunk.');
        }
        // Snapshot every field and byte before hashing or networking can yield.
        const bytes = new Uint8Array(input.bytes);
        const acknowledgement: ReplayChunkAcknowledgement = {protocolVersion: 2, sessionId: fixed.sessionId,
            chunkIndex: input.chunkIndex, digest: input.digest, eventCount: input.eventCount, bytesCompressed: bytes.byteLength};
        const parameters = new URLSearchParams({protocolVersion: '2', chunkIndex: String(acknowledgement.chunkIndex),
            eventCount: String(acknowledgement.eventCount), digest: acknowledgement.digest});
        if (input.startTimestamp !== null) parameters.set('startTimestamp', String(input.startTimestamp));
        if (input.endTimestamp !== null) parameters.set('endTimestamp', String(input.endTimestamp));
        return this.run(fixed, options, async context => {
            const hash = await context.wait(crypto.subtle.digest('SHA-256', bytes));
            context.checkCredential();
            const digest = Array.from(new Uint8Array(hash), value => value.toString(16).padStart(2, '0')).join('');
            if (digest !== acknowledgement.digest) throw new ReplayTransportError('invalid-request', 'Prepared replay bytes do not match their digest.');
            const value = await context.request(this.sessionPath(fixed) + '/chunks?' + parameters,
                {method: 'POST', body: bytes, headers: {'Content-Type': 'application/octet-stream'}});
            if (!record(value) || Object.entries(acknowledgement).some(([key, field]) => value[key] !== field)) throw invalidResponse();
            return acknowledgement;
        });
    }

    complete(expected: ReplayOutboxScope, input: ReplayCompletion, options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        const fixed = scope(expected);
        if (!input || !integer(input.expectedChunkCount) || !integer(input.expectedTotalEvents)) {
            throw new ReplayTransportError('invalid-request', 'Invalid replay completion counts.');
        }
        const counts = {expectedChunkCount: input.expectedChunkCount, expectedTotalEvents: input.expectedTotalEvents};
        const body = encodedJson({protocolVersion: 2, summary: input.summary, ...counts});
        return this.run(fixed, options, async context => {
            const value = checkedSession(await context.request(this.sessionPath(fixed) + '?protocolVersion=2', {method: 'PATCH', body}), fixed);
            if (value.status !== 'completed' || value.chunkCount !== counts.expectedChunkCount ||
                value.totalEvents !== counts.expectedTotalEvents) throw invalidResponse();
            return value;
        });
    }

    heartbeat(expected: ReplayOutboxScope, options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        const fixed = scope(expected);
        return this.run(fixed, options, async context => {
            await context.request(this.sessionPath(fixed) + '/heartbeat?protocolVersion=2', {method: 'POST'}, true);
            const value = checkedSession(await context.request(this.statusPath(fixed)), fixed);
            if (value.status !== 'recording') throw new ReplayTransportError('session-closed', 'Replay session is not recording.', 409);
            return value;
        });
    }

    private sessionPath(expected: ReplayOutboxScope): string { return '/replay/sessions/' + encodeURIComponent(expected.sessionId); }
    private statusPath(expected: ReplayOutboxScope): string { return this.sessionPath(expected) + '?fields=status&protocolVersion=2'; }

    private async run<T>(expected: Readonly<ReplayIdentity>, options: ReplayTransportOptions, work: (context: {
        request: (path: string, init?: RequestInit, noContent?: boolean) => Promise<unknown>;
        wait: <U>(pending: Promise<U>) => Promise<U>;
        checkCredential: () => string;
    }) => Promise<T>): Promise<T> {
        const controller = new AbortController();
        const abort = () => controller.abort(new ReplayTransportError('aborted', 'Replay request cancelled.'));
        const callerSignal = options.signal;
        const useKeepalive = options.keepalive === true;
        callerSignal?.addEventListener('abort', abort, {once: true});
        if (callerSignal?.aborted) abort();
        const timer = setTimeout(() => controller.abort(new ReplayTransportError('timeout', 'Replay request timed out.')), this.timeoutMs);
        const active = () => {if (controller.signal.aborted) throw controller.signal.reason;};
        const checkCredential = () => {
            active();
            let token: string | null | undefined;
            try {token = this.credential(expected);}
            catch {throw new ReplayTransportError('credential', 'Replay identity is no longer ready.');}
            if (typeof token !== 'string' || !token.trim() || /[\r\n]/.test(token)) {
                throw new ReplayTransportError('credential', 'Replay identity is no longer ready.');
            }
            active();
            return token;
        };
        const wait = <U>(pending: Promise<U>): Promise<U> => {
            return new Promise<U>((resolve, reject) => {
                let settled = false;
                const finish = (done: (value: U) => void, value: U) => {
                    if (settled) return;
                    settled = true;
                    controller.signal.removeEventListener('abort', cancelled);
                    done(value);
                };
                const cancelled = () => finish(reject, controller.signal.reason);
                controller.signal.addEventListener('abort', cancelled, {once: true});
                // Always attach rejection handling, even when a synchronous
                // fetch implementation aborted before returning its promise.
                pending.then(value => finish(resolve, value), error => finish(reject, error));
                if (controller.signal.aborted) cancelled();
            });
        };
        const request = async (path: string, init: RequestInit = {}, noContent = false): Promise<unknown> => {
            checkCredential();
            const headers = new Headers(init.headers);
            headers.set('Accept', 'application/json');
            if (typeof init.body === 'string') headers.set('Content-Type', 'application/json');
            const length = typeof init.body === 'string' ? new TextEncoder().encode(init.body).byteLength :
                init.body instanceof Uint8Array ? init.body.byteLength : 0;
            const pending = makeSecureApiCall(this.baseUrl + path, {...init, headers,
                signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store',
                keepalive: useKeepalive && length <= 60000}, checkCredential, this.fetcher).then(response => {
                if (controller.signal.aborted) void response.body?.cancel().catch(() => undefined);
                return response;
            });
            const response = await wait(pending);
            try {checkCredential();}
            catch (error) {void response.body?.cancel().catch(() => undefined); throw error;}
            const readJson = async (): Promise<unknown> => {
                const contentLength = response.headers.get('Content-Length');
                if (contentLength !== null && (!/^\d+$/.test(contentLength) || Number(contentLength) > REPLAY_MAX_RESPONSE_BYTES)) {
                    void response.body?.cancel().catch(() => undefined);
                    throw new ReplayTransportError('response-too-large', 'Replay response exceeds the metadata limit.');
                }
                if (!response.body || !/^application\/json(?:\s*;|$)/i.test(response.headers.get('Content-Type') ?? '')) {
                    void response.body?.cancel().catch(() => undefined); throw invalidResponse();
                }
                const reader = response.body.getReader();
                const cancelBody = () => {void reader.cancel().catch(() => undefined);};
                controller.signal.addEventListener('abort', cancelBody, {once: true});
                const bytes = new Uint8Array(REPLAY_MAX_RESPONSE_BYTES);
                let used = 0;
                try {
                    while (true) {
                        const part = await wait(reader.read());
                        checkCredential();
                        if (part.done) break;
                        if (!(part.value instanceof Uint8Array) || part.value.byteLength > bytes.byteLength - used) {
                            throw new ReplayTransportError('response-too-large', 'Replay response exceeds the metadata limit.');
                        }
                        bytes.set(part.value, used); used += part.value.byteLength;
                    }
                    try {return JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(bytes.subarray(0, used)));}
                    catch {throw invalidResponse();}
                } finally {
                    controller.signal.removeEventListener('abort', cancelBody);
                    cancelBody();
                    reader.releaseLock();
                }
            };
            if (!response.ok) {
                const codes: Partial<Record<number, TransportCode>> = {403: 'forbidden', 404: 'not-found', 409: 'conflict', 410: 'gone', 426: 'upgrade-required'};
                let serverCode: string | undefined;
                try {
                    const errorBody = await readJson();
                    if (record(errorBody) && typeof errorBody.code === 'string' && errorBody.code.length <= 128) {
                        serverCode = errorBody.code;
                    }
                } catch { /* A generic/proxy/bodyless error is never authoritative disposal proof. */ }
                throw new ReplayTransportError(codes[response.status] ?? 'http', 'Replay request was rejected.', response.status, serverCode,
                    replayRetryAfter(response.headers.get('Retry-After')));
            }
            if (noContent) {
                if (response.status !== 204) {void response.body?.cancel().catch(() => undefined); throw invalidResponse();}
                return undefined;
            }
            return readJson();
        };
        try {
            if (this.renewCredential) await wait(this.renewCredential(expected, controller.signal));
            checkCredential();
            const result = await work({request, wait, checkCredential});
            checkCredential();
            return result;
        } catch (error) {
            const failure = controller.signal.aborted ? controller.signal.reason : error instanceof ReplayTransportError
                ? error : new ReplayTransportError('network', 'Replay request could not be completed.');
            controller.abort(failure);
            throw failure;
        } finally {
            clearTimeout(timer);
            callerSignal?.removeEventListener('abort', abort);
        }
    }
}
